import { useEffect, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

export interface UnreadCounts {
  /** Total unread support messages waiting on the signed-in user. */
  support: number;
  /** Unread invoices for clients (admin-issued). 0 for admins. */
  billing: number;
  /** Pending domain requests waiting on admin review. 0 for clients. */
  domainRequests: number;
  loading: boolean;
}

/**
 * Real-time unread counts driving the sidebar badges.
 *
 * Sources:
 *   - support  → sum of `clientUnreadCount` (clients) or `adminUnreadCount`
 *                (admins) across visible support tickets
 *   - billing  → `unreadInvoiceCount` on the signed-in client's own doc;
 *                bumped whenever an admin sends them an invoice and zeroed
 *                when they open /billing.
 */
export function useUnreadCounts(): UnreadCounts {
  const { role, clientId } = useAuth();
  const [support, setSupport] = useState(0);
  const [billing, setBilling] = useState(0);
  const [domainRequests, setDomainRequests] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!role) {
      setSupport(0);
      setBilling(0);
      setDomainRequests(0);
      setLoading(false);
      return;
    }

    // Support listener (admin sees all tickets, client sees their own).
    const supportQ =
      role === "admin"
        ? query(collection(db, "supportRequests"))
        : clientId
        ? query(
            collection(db, "supportRequests"),
            where("clientId", "==", clientId)
          )
        : null;

    let supportLoaded = supportQ ? false : true;
    let billingLoaded = role === "client" && clientId ? false : true;
    let domainRequestsLoaded = role === "admin" ? false : true;
    function maybeFinishLoading() {
      if (supportLoaded && billingLoaded && domainRequestsLoaded) setLoading(false);
    }

    const unsubs: Array<() => void> = [];

    if (supportQ) {
      const u = onSnapshot(
        supportQ,
        (snap) => {
          let total = 0;
          const field = role === "admin" ? "adminUnreadCount" : "clientUnreadCount";
          snap.forEach((d) => {
            const v = d.data()[field];
            if (typeof v === "number") total += v;
          });
          setSupport(total);
          supportLoaded = true;
          maybeFinishLoading();
        },
        (err) => {
          console.error("useUnreadCounts.support:", err);
          setSupport(0);
          supportLoaded = true;
          maybeFinishLoading();
        }
      );
      unsubs.push(u);
    } else {
      setSupport(0);
    }

    // Domain-request listener — admin only. Counts requests that still need
    // admin action: pending review, paid awaiting domain registration, and
    // domain-done awaiting mailbox provisioning. Terminal states (completed,
    // rejected) and "approved/invoiced" (waiting on client to pay) don't
    // count toward the badge because there's no admin action to take.
    if (role === "admin") {
      const u = onSnapshot(
        query(
          collection(db, "domainRequests"),
          where("status", "in", ["pending", "paid", "domain_done"])
        ),
        (snap) => {
          setDomainRequests(snap.size);
          domainRequestsLoaded = true;
          maybeFinishLoading();
        },
        (err) => {
          console.error("useUnreadCounts.domainRequests:", err);
          setDomainRequests(0);
          domainRequestsLoaded = true;
          maybeFinishLoading();
        }
      );
      unsubs.push(u);
    } else {
      setDomainRequests(0);
    }

    // Billing listener — only meaningful for clients.
    if (role === "client" && clientId) {
      const u = onSnapshot(
        doc(db, "clients", clientId),
        (snap) => {
          const v = (snap.data() ?? {}).unreadInvoiceCount;
          setBilling(typeof v === "number" ? v : 0);
          billingLoaded = true;
          maybeFinishLoading();
        },
        (err) => {
          console.error("useUnreadCounts.billing:", err);
          setBilling(0);
          billingLoaded = true;
          maybeFinishLoading();
        }
      );
      unsubs.push(u);
    } else {
      setBilling(0);
    }

    return () => {
      for (const u of unsubs) u();
    };
  }, [role, clientId]);

  return { support, billing, domainRequests, loading };
}
